import { type ReactNode, useEffect, useRef, useState } from "react";

import { ActionIcon, Box, Button, Drawer, Flex, Group, Title, Tooltip } from "@mantine/core";

import { IconX } from "@tabler/icons-react";

import { Loading } from "@/components/Loading";

import { LaborFlowsheetUtcDateAnchorContext } from "./LaborFlowsheetUtcDateAnchorContext";

interface LaborFlowsheetDrawerShellProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  submitButtonText: string;
  formId: string;
  onSubmit: (event: React.FormEvent) => void;
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  headerSection?: ReactNode;
  children: ReactNode;
  /** UTC calendar date anchors for edit-mode time display (linkId → YYYY-MM-DD). */
  dateTimeUtcDateAnchors?: Record<string, string>;
}

export function LaborFlowsheetDrawerShell({
  opened,
  onClose,
  title,
  submitButtonText,
  formId,
  onSubmit,
  isLoading,
  isSubmitting,
  error,
  headerSection,
  children,
  dateTimeUtcDateAnchors = {},
}: LaborFlowsheetDrawerShellProps) {
  const [contentSessionKey, setContentSessionKey] = useState(0);
  const wasOpenedRef = useRef(false);

  useEffect(() => {
    if (opened && !wasOpenedRef.current) {
      setContentSessionKey((currentKey) => currentKey + 1);
    }
    wasOpenedRef.current = opened;
  }, [opened]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onSubmit(event);
  };

  return (
    <Drawer
      size={500}
      opened={opened}
      position="right"
      onClose={onClose}
      keepMounted={false}
      closeOnEscape={false}
      withCloseButton={false}
      closeOnClickOutside={false}
      styles={{
        body: { padding: 0 },
        content: { background: "white" },
      }}
    >
      <form id={formId} onSubmit={handleSubmit} data-testid="labor-flowsheet-form">
        <Box
          px="xs"
          bg="gray.0"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1000,
          }}
        >
          <Group p="xs" justify="space-between">
            <Group gap="xs">
              <Tooltip label="Close">
                <ActionIcon
                  c="dark.9"
                  variant="subtle"
                  onClick={onClose}
                  data-testid="labor-flowsheet-drawer-close"
                >
                  <IconX size={16} />
                </ActionIcon>
              </Tooltip>
              <Title order={4} c="dark.9">
                {title}
              </Title>
            </Group>
            <Button
              type="submit"
              form={formId}
              disabled={isLoading || !!error}
              loading={isLoading || isSubmitting}
              data-testid="add-to-flowsheet-drawer-button"
            >
              {submitButtonText}
            </Button>
          </Group>
        </Box>
        {isLoading ? (
          <Loading />
        ) : error ? (
          <Box p="md" c="red">
            <Title order={5}>Error loading form</Title>
            <p>{error}</p>
          </Box>
        ) : (
          <Flex p="lg" gap="lg" direction="column" key={contentSessionKey}>
            <LaborFlowsheetUtcDateAnchorContext.Provider value={dateTimeUtcDateAnchors}>
              {headerSection}
              {children}
            </LaborFlowsheetUtcDateAnchorContext.Provider>
          </Flex>
        )}
      </form>
    </Drawer>
  );
}
